import { CAR_BY_ID } from '../../shared/cars.js';
import { ARCADE_PRESETS, arcadeCareerEligible, normaliseArcadePreset } from '../../shared/arcade-presets.js';
import { raceWagerDefinition } from '../../shared/gameplay-fun.js';
import { raceEventMode } from '../../shared/race-events.js';
import type { MapDefinition, PublicRoom, RaceSnapshot } from '../../shared/types.js';
import type { GhostStatus } from '../game/lap-ghost-store.js';
import { formatNumber, t } from '../i18n/index.js';
import { currentNightTourEvent, nightTourDefinition, nightTourEvents, type NightTourState } from '../night-tour.js';
import { nextCareerReward, progressionMeter, rankTitle, type ProgressionUpdate } from '../progression.js';
import { RuntimeScope } from '../runtime-scope.js';
import { masteryPaintForCar, type VehicleMasteryUpdate } from '../vehicle-mastery.js';
import { escapeHtml } from './html.js';
import { formatLapTime, formatSectorTime, heatRunDisplayScore, medalLabel, ordinal, resultStatus } from './race-format.js';

export interface ResultsPresentationModel {
  readonly snapshot: RaceSnapshot;
  readonly room: PublicRoom | null;
  readonly playerId: string | undefined;
  readonly currentGhostStatus: GhostStatus | null;
  readonly progressionUpdate: ProgressionUpdate | null;
  readonly vehicleMasteryUpdate: VehicleMasteryUpdate | null;
  readonly activeRaceMap: MapDefinition | null;
  readonly maps: readonly MapDefinition[];
  readonly nightTour: NightTourState;
  readonly localRace: boolean;
}

/** Owns results DOM and animation lifetime; career persistence stays with the app. */
export class ResultsPresentation {
  private animationScope: RuntimeScope | null = null;

  constructor(private readonly root: HTMLElement) {}

  reset(): void {
    this.animationScope?.dispose();
    this.animationScope = null;
  }

  focus(): void {
    const results = this.root.querySelector<HTMLElement>('#results-overlay');
    results?.querySelector<HTMLButtonElement>('.results-actions button:not([hidden])')?.focus({ preventScroll: true });
    const card = results?.querySelector<HTMLElement>('.results-card');
    if (card) card.scrollTop = 0;
  }

  render(model: ResultsPresentationModel, animate = true): void {
    this.reset();
    this.animationScope = new RuntimeScope();
    const snapshot = model.snapshot;
    const mustElement = <T extends Element = HTMLElement>(selector: string): T => {
      const element = this.root.querySelector<T>(selector);
      if (!element) throw new Error(`Expected results element ${selector}`);
      return element;
    };
    const results = mustElement('#results-overlay');
    results.classList.remove('hidden');
    mustElement('#race-stage').classList.add('results-visible');
    const mode = model.room ? raceEventMode(model.room.settings) : 'circuit';
    const timeTrial = mode === 'time-trial';
    const resultPlayer = snapshot.racers.find((racer) => racer.id === model.playerId);
    mustElement('#results-title').textContent = model.room?.settings.dailyChallengeId
      ? t('DAILY CHALLENGE COMPLETE')
      : timeTrial
      ? t('TIME TRIAL COMPLETE')
      : mode === 'sprint' ? t('STREET SPRINT RESULTS')
        : mode === 'style-rush' ? t('STYLE RUSH RESULTS')
          : mode === 'elimination' ? t('LAST DRIVER STANDING')
            : mode === 'heat-run' ? (resultPlayer?.completedLaps ?? 0) > 0 ? t('HEAT BANKED') : t('HEAT LOST')
              : mode === 'road-rage' ? t('ROAD RAGE RESULTS')
            : t('FINAL ORDER');
    const resultList = mustElement<HTMLOListElement>('#results-list');
    resultList.hidden = timeTrial;
    const classifiedRacers = snapshot.racers.filter((racer) => !racer.pursuit);
    const ordered = [...classifiedRacers].sort((a, b) => a.place - b.place);
    this.root.querySelector('.results-podium')?.remove();
    this.root.querySelector('.results-hero')?.remove();
    const heroPlayer = classifiedRacers.find((racer) => racer.id === model.playerId);
    if (!timeTrial && heroPlayer) {
      // The player's own place leads the card (beside the title on desktop, above the list on touch).
      const finished = heroPlayer.finishedAt !== null;
      // "6TH" lifts the letters; German "6." keeps its full stop on the line.
      const heroSuffix = ordinal(heroPlayer.place).replace(/^\d+/, '');
      mustElement('#results-title').insertAdjacentHTML('afterend', `<div class="results-hero" data-place="${heroPlayer.place}"><b>${heroPlayer.place}${heroSuffix && /^\p{L}/u.test(heroSuffix) ? `<sup>${heroSuffix}</sup>` : heroSuffix}</b><span><small>${finished ? t('YOUR FINISH') : t('YOUR RESULT')}</small><em>${t('OF {total} · {car}', { total: classifiedRacers.length, car: escapeHtml(CAR_BY_ID.get(heroPlayer.carId)?.name ?? '') })}</em></span></div>`);
    }
    if (!timeTrial && ordered.length >= 3) {
      // Podium moment: 2nd/1st/3rd visual order, staggered pop-in via CSS.
      const podiumOrder = [ordered[1]!, ordered[0]!, ordered[2]!];
      resultList.insertAdjacentHTML('beforebegin', `<div class="results-podium">${podiumOrder.map((racer) => `
        <article data-place="${racer.place}" class="${racer.id === model.playerId ? 'you' : ''}"><i>${racer.place}</i><b>${escapeHtml(racer.name)}</b><small>${escapeHtml(CAR_BY_ID.get(racer.carId)?.name ?? '')}</small></article>`).join('')}</div>`);
    }
    const winnerFinishedAt = ordered[0]?.finishedAt ?? null;
    resultList.innerHTML = ordered.map((racer) => `
      <li class="${racer.id === model.playerId ? 'you' : ''}"><b>${String(racer.place).padStart(2, '0')}</b><span>${escapeHtml(racer.name)}</span><small>${escapeHtml(CAR_BY_ID.get(racer.carId)?.name ?? '')} · ${mode === 'heat-run'
        ? t('HEAT {heat} · SCORE {score}', { heat: racer.completedLaps, score: formatNumber(heatRunDisplayScore(racer)) })
        : mode === 'road-rage' ? t('{takedowns} TAKEDOWNS · {points} PTS', { takedowns: racer.takedownCount ?? 0, points: formatNumber(racer.roadRageScore ?? 0) })
          : t('STYLE {score}', { score: formatNumber(Math.round(racer.styleScore)) })}</small>${resultStatus(mode, racer, winnerFinishedAt)}</li>`).join('');
    const trialResult = mustElement<HTMLElement>('#results-time-trial');
    this.root.querySelector('.wager-result')?.remove();
    trialResult.hidden = !timeTrial;
    if (timeTrial) {
      const status = model.currentGhostStatus;
      const medal = status ? medalLabel(status.lastLapMs, status.targets) : 'NO MEDAL';
      trialResult.dataset.medal = medal.toLowerCase().replace(' ', '-');
      trialResult.innerHTML = status ? `
        <div class="trial-medal"><i>${medal === 'NO MEDAL' ? '◇' : medal === 'GOLD' ? '◆' : '⬢'}</i><span><small>${t('LATEST FLYING LAP')}</small><b>${status.lastLapMs ? formatLapTime(status.lastLapMs) : '--:--.---'}</b><em>${t(medal)}</em></span></div>
        <dl><div><dt>${t('PERSONAL BEST')}</dt><dd>${status.bestLapMs ? formatLapTime(status.bestLapMs) : '--:--.---'}</dd></div>${status.bestSectorTimesMs.map((time, index) => `<div><dt>${t('SECTOR {n}', { n: index + 1 })}</dt><dd>${formatSectorTime(time)}</dd></div>`).join('')}</dl>
        <p>${t('GOLD {gold} · SILVER {silver} · BRONZE {bronze}', { gold: formatLapTime(status.targets.goldMs), silver: formatLapTime(status.targets.silverMs), bronze: formatLapTime(status.targets.bronzeMs) })}</p>` : '';
    }
    if (resultPlayer?.wagerKind) {
      const wager = raceWagerDefinition(resultPlayer.wagerKind);
      if (wager) {
        trialResult.insertAdjacentHTML('afterend', `<aside class="wager-result" data-status="${resultPlayer.wagerStatus}"><i>${resultPlayer.wagerStatus === 'complete' ? '✓' : '×'}</i><span><small>${t('OPTIONAL WAGER')}</small><b>${escapeHtml(t(wager.name))}</b><em>${resultPlayer.wagerStatus === 'complete' ? t('WON · +{xp} XP', { xp: wager.xp }) : t('MISSED · {progress} / {target}', { progress: Math.floor(resultPlayer.wagerProgress ?? 0), target: resultPlayer.wagerTarget ?? wager.target })}</em></span></aside>`);
      }
    }
    const career = mustElement('#results-career');
    career.replaceChildren();
    const update = model.progressionUpdate;
    if (!arcadeCareerEligible(model.room?.settings.arcadePreset)) {
      const preset = ARCADE_PRESETS.find((candidate) => candidate.id === normaliseArcadePreset(model.room?.settings.arcadePreset));
      career.innerHTML = `<header class="career-result-head"><div><small>${t('ARCADE LAB')}</small><b>${escapeHtml(t(preset?.name ?? 'CUSTOM EVENT'))}</b></div><strong>${t('NO CAREER SAVE')}</strong></header><p class="career-result-empty">${t('This playful ruleset keeps career XP, records, ghosts and vehicle mastery untouched.')}</p>`;
    } else if (update) {
      const levelUp = update.level > update.previousLevel;
      const meter = progressionMeter(update.state.xp);
      const nextReward = nextCareerReward(update.level);
      const masteryMap = model.activeRaceMap?.id === update.mastery?.mapId
        ? model.activeRaceMap
        : model.maps.find((map) => map.id === update.mastery?.mapId);
      const rivalHighlights = [...update.rivals]
        .sort((first, second) => Number(first.result === 'won') - Number(second.result === 'won') || second.record.races - first.record.races)
        .slice(0, 3);
      career.innerHTML = `
        <header class="career-result-head"><div><small>${t('LOCAL STREET CAREER')}</small><b>${t('LV {level} // {rank}', { level: String(update.level).padStart(2, '0'), rank: t(rankTitle(update.level)) })}</b></div><strong>${update.duplicate ? t('ALREADY BANKED') : `+${formatNumber(update.xpEarned)} XP`}${levelUp ? `<em>${t('LEVEL UP')}</em>` : ''}</strong></header>
        <div class="career-result-meter"><span><i style="width:${meter.percent.toFixed(1)}%"></i></span><small>${meter.maxed ? t('MAX RANK') : t('{current} / {required} TO NEXT RANK', { current: formatNumber(meter.current), required: formatNumber(meter.required) })}</small></div>
        ${update.xpBreakdown.length > 0 ? `<ul class="career-xp-breakdown">${update.xpBreakdown.map((entry) => `<li><i>+</i><b>${t(entry.label)}</b><small>+${formatNumber(entry.xp)} XP</small></li>`).join('')}</ul>` : `<p class="career-result-empty">${t('Finish, style, and take risks to bank career XP.')}</p>`}
        ${update.completedContracts.length > 0 || update.completed.length > 0 ? `<section class="career-result-contracts"><small>${t('CONTRACTS CLEARED')}</small>${[...update.completed.map((challenge) => ({ name: t(challenge.name), reward: challenge.reward })), ...update.completedContracts.map((contract) => ({ name: `${t(contract.cadence.toUpperCase())} // ${t(contract.name)}`, reward: contract.reward }))].map((contract) => `<span><i>✓</i><b>${contract.name}</b><em>+${formatNumber(contract.reward)}</em></span>`).join('')}</section>` : ''}
        ${update.mastery ? `<section class="career-result-mastery" data-tier="${update.mastery.after.tier}"><div><small>${t('TRACK MASTERY // {map}', { map: escapeHtml(masteryMap?.name ?? update.mastery.mapId) })}</small><b>${update.mastery.after.tier === 'none' ? t('UNRANKED') : t(update.mastery.after.tier.toUpperCase())} · ${update.mastery.after.completed}/${update.mastery.after.total}</b></div><span><i style="width:${update.mastery.after.percent.toFixed(1)}%"></i></span>${update.mastery.newGoals.length > 0 ? `<p>${t('NEW // {goals}', { goals: update.mastery.newGoals.map((goal) => t(goal)).join(' · ') })}</p>` : `<p>${t('Keep racing this district to raise mastery.')}</p>`}</section>` : ''}
        ${rivalHighlights.length > 0 ? `<section class="career-result-rivals"><small>${t('RIVAL REPORT')}</small>${rivalHighlights.map((rival) => `<span data-result="${rival.result}"><i>${rival.result === 'won' ? t('W') : t('L')}</i><b>${escapeHtml(rival.name)}</b><em>${rival.record.winsAgainst}-${rival.record.lossesAgainst} · ${rival.record.streak > 0 ? t('{n} WIN STREAK', { n: Math.abs(rival.record.streak) }) : t('{n} LOSS STREAK', { n: Math.abs(rival.record.streak) })}</em></span>`).join('')}</section>` : ''}
        <footer>${nextReward ? `<span>${t('NEXT UNLOCK')}</span><b>${t(nextReward.name)}</b><small>${t('LEVEL {level}', { level: nextReward.level })}</small>` : `<b>${t('ALL CAREER REWARDS UNLOCKED')}</b>`}</footer>`;
      if (animate) this.animateResultsNumbers(career, update.duplicate ? null : update.xpEarned);
    }
    const vehicleUpdate = model.vehicleMasteryUpdate;
    if (vehicleUpdate && arcadeCareerEligible(model.room?.settings.arcadePreset)) {
      const car = CAR_BY_ID.get(vehicleUpdate.carId);
      const paint = masteryPaintForCar(vehicleUpdate.carId);
      career.insertAdjacentHTML('beforeend', `
        <section class="career-result-mastery vehicle-result-mastery" data-tier="${vehicleUpdate.paintUnlocked ? 'gold' : 'none'}">
          <div><small>${t('VEHICLE MASTERY // {car}', { car: escapeHtml(car?.name ?? vehicleUpdate.carId) })}</small><b>${t('{done} / {total} GOALS', { done: vehicleUpdate.goals.filter((goal) => goal.complete).length, total: vehicleUpdate.goals.length })}</b></div>
          <span><i style="width:${(vehicleUpdate.goals.filter((goal) => goal.complete).length / vehicleUpdate.goals.length * 100).toFixed(1)}%"></i></span>
          <p>${vehicleUpdate.newlyUnlocked && paint
            ? t('UNLOCKED // {paint} MASTERY PAINT', { paint: escapeHtml(t(paint.toUpperCase())) })
            : vehicleUpdate.newlyCompleted.length > 0
              ? t('NEW // {goals}', { goals: vehicleUpdate.newlyCompleted.map((goal) => escapeHtml(t(goal.name))).join(' · ') })
              : t('Drive its signature to complete all three goals.')}</p>
        </section>`);
    }
    if (model.room?.settings.tourId === model.nightTour.tourId) {
      const tourDefinition = nightTourDefinition(model.nightTour.tourId);
      const tourEvents = nightTourEvents(model.nightTour);
      const tourStage = model.room.settings.tourStage ?? model.nightTour.eventIndex;
      const stageBanked = model.nightTour.eventIndex > tourStage;
      const latest = stageBanked ? model.nightTour.results.at(-1) : undefined;
      const next = currentNightTourEvent(model.nightTour);
      career.insertAdjacentHTML('beforeend', `
        <section class="tour-result" data-complete="${model.nightTour.completed}">
          <header><small>${t('NIGHT TOUR // {tour}', { tour: escapeHtml(t(tourDefinition.name)) })}</small><b>${model.nightTour.completed ? t('TOUR COMPLETE') : t('STAGE {stage} / {total}', { stage: Math.min(tourStage + 1, tourEvents.length), total: tourEvents.length })}</b></header>
          <div><span><small>${t('YOU')}</small><b>${t('{points} PTS', { points: formatNumber(model.nightTour.playerPoints) })}</b></span><i>${latest ? `+${latest.points}` : t('DNF')}</i><span><small>${t('RIVAL BOSSES')}</small><b>${t('{points} PTS', { points: formatNumber(model.nightTour.rivalPoints) })}</b></span></div>
          <p>${model.nightTour.completed ? t('{reward} UNLOCKED · SERIES COMPLETE.', { reward: escapeHtml(t(tourDefinition.reward)) }) : stageBanked ? t('NEXT // {event}', { event: escapeHtml(t(next?.title ?? '')) }) : t('RETRY // {event}', { event: escapeHtml(t(next?.title ?? '')) })}</p>
        </section>`);
    }
    const solo = model.room?.playerCount === 1;
    mustElement<HTMLButtonElement>('#results-restart').hidden = !solo;
    const lobbyButton = mustElement<HTMLButtonElement>('#results-lobby');
    lobbyButton.disabled = false;
    lobbyButton.innerHTML = model.localRace
      ? `${t('BACK TO LOBBY')} <b>↺</b>`
      : `${t('BACK TO LOBBY')} <b>↺</b><small>${t('Same crew, next race')}</small>`;
    mustElement<HTMLButtonElement>('#results-leave').textContent = model.localRace ? t('MAIN MENU') : t('LEAVE SESSION');
    const nextButton = mustElement<HTMLButtonElement>('#results-next');
    const tourContinues = model.room?.settings.tourId === model.nightTour.tourId && !model.nightTour.completed;
    const retryTourStage = tourContinues
      && model.room?.settings.tourStage !== undefined
      && model.nightTour.eventIndex === model.room.settings.tourStage;
    nextButton.hidden = tourContinues ? false : !model.localRace || model.maps.length < 2;
    nextButton.textContent = tourContinues
      ? retryTourStage ? t('RETRY TOUR EVENT ↻') : t('NEXT TOUR EVENT →')
      : t('NEXT EVENT →');
  }

  private animateResultsNumbers(career: HTMLElement, xp: number | null): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const scope = this.animationScope;
    if (!scope) return;
    const headline = career.querySelector('.career-result-head strong');
    const textNode = headline?.firstChild;
    if (headline && textNode && xp !== null) {
      const started = performance.now();
      const tick = (now: number): void => {
        if (!this.root.contains(headline)) return;
        const progress = Math.min(1, Math.max(0, (now - started) / 950));
        textNode.textContent = `+${formatNumber(Math.round(xp * (1 - (1 - progress) ** 3)))} XP`;
        if (progress < 1) scope.animationFrame(tick);
      };
      scope.animationFrame(tick);
    }
    for (const bar of career.querySelectorAll<HTMLElement>('.career-result-meter i, .career-result-mastery > span i')) {
      const width = bar.style.width;
      bar.style.transition = 'none';
      bar.style.width = '0%';
      scope.animationFrame(() => {
        scope.animationFrame(() => {
          bar.style.transition = '';
          bar.style.width = width;
        });
      });
    }
  }
}
